import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { getTodayInZambia, getNowInZambiaForInput, formatInZambia, getZambiaDayBounds, zambiaDateTimeToISO } from "@/lib/zambia-time";
import { useBranchContext } from "@/contexts/BranchContext";
import { useBranches } from "@/hooks/useBranches";
import { useEmployeeViewSettings } from "@/hooks/useEmployeeViewSettings";
import { RestrictedPageNotice } from "@/components/RestrictedPageNotice";
import { useEmployeeNotificationPrefs } from "@/hooks/useEmployeeNotificationPrefs";

interface LedgerRow {
  id: string;
  item_name: string | null;
  quantity: number | null;
  unit_price: number | null;
  total_price: number | null;
  discount: number | null;
  final_amount: number | null;
  payment_method: string | null;
  created_at: string;
}

interface InventoryOption {
  id: string;
  name: string;
  selling_price: number;
  current_stock: number;
  branch_id?: string | null;
}

/** State passed from Stock Check "Record Sale" button */
interface ManualEntryLocationState {
  preselectedItem?: InventoryOption;
}

export default function ManualEntryPage() {
  const { user, businessId, profile, isFounder, role } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const locationState = location.state as ManualEntryLocationState | undefined;
  const { selectedBranchId } = useBranchContext();
  const { branches } = useBranches();
  const { settings: employeeViewSettings } = useEmployeeViewSettings();
  const { prefs: notificationPrefs } = useEmployeeNotificationPrefs();

  const [inventory, setInventory] = useState<InventoryOption[]>([]);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryOption | null>(null);
  const [customItemName, setCustomItemName] = useState("");
  const [quantity, setQuantity] = useState<number | "">("");
  const [unitPrice, setUnitPrice] = useState<number | "">("");
  const [discount, setDiscount] = useState<number | "">("");
  const [paymentMethod, setPaymentMethod] = useState<string>("Cash");
  const [createdAt, setCreatedAt] = useState(() => getNowInZambiaForInput());

  const [submitting, setSubmitting] = useState(false);

  const PAYMENT_METHODS = ["Cash", "Airtel Money", "MTN MoMo", "Card", "Credit (Tab)", "Other"] as const;

  const [selectedDate, setSelectedDate] = useState(() => getTodayInZambia());
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const itemName = selectedItem ? selectedItem.name : (customItemName || "");
  const itemId = selectedItem?.id ?? null;

  const parsedQuantity = typeof quantity === "number" ? quantity : Number(quantity || 0);
  const parsedUnitPrice = typeof unitPrice === "number" ? unitPrice : Number(unitPrice || 0);
  const parsedDiscount = typeof discount === "number" ? discount : Number(discount || 0);

  const totalPrice = useMemo(() => parsedQuantity * parsedUnitPrice, [parsedQuantity, parsedUnitPrice]);
  const finalAmount = useMemo(() => Math.max(totalPrice - parsedDiscount, 0), [totalPrice, parsedDiscount]);

  if (role === "employee" && !employeeViewSettings.allowEmployeeRecordSales) {
    return (
      <RestrictedPageNotice
        title="Sales entry is disabled"
        description="Your manager has turned off sales recording for employee accounts."
      />
    );
  }

  const effectiveBusinessId = businessId ?? profile?.business_id ?? null;
  const fetchInventory = async () => {
    if (!effectiveBusinessId) return;
    let baseQuery = supabase
      .from("inventory")
      .select("id, name, selling_price, current_stock, branch_id")
      .order("name")
      .eq("business_id", effectiveBusinessId);
    if (selectedBranchId !== "all") baseQuery = baseQuery.eq("branch_id", selectedBranchId);
    const { data } = await baseQuery;
    setInventory((data ?? []) as InventoryOption[]);
  };

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBusinessId, selectedBranchId]);

  useEffect(() => {
    const preselected = locationState?.preselectedItem;
    if (preselected?.id && preselected?.name != null) {
      setSelectedItem({
        id: preselected.id,
        name: preselected.name,
        selling_price: Number(preselected.selling_price) ?? 0,
        current_stock: Number(preselected.current_stock) ?? 0,
      });
      setUnitPrice(Number(preselected.selling_price) ?? 0);
      setCustomItemName("");
    }
  }, [locationState?.preselectedItem?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({ title: "Not signed in", description: "You must be signed in to record sales.", variant: "destructive" });
      return;
    }
    if (!effectiveBusinessId && !isFounder) {
      toast({
        title: "No business selected",
        description: "Attach your profile to a business before recording sales.",
        variant: "destructive",
      });
      return;
    }
    if (!itemName?.trim()) {
      toast({ title: "Item required", description: "Select an item from inventory or enter a custom item name.", variant: "destructive" });
      return;
    }

    if (selectedItem && parsedQuantity > selectedItem.current_stock) {
      toast({
        title: "Not enough stock available",
        description: `Only ${selectedItem.current_stock} in stock for "${selectedItem.name}".`,
        variant: "destructive",
      });
      return;
    }
    if (branches.length > 1 && selectedBranchId === "all") {
      toast({
        title: "Select location",
        description: "Choose a location from the header before recording a sale.",
        variant: "destructive",
      });
      return;
    }

    const bid = effectiveBusinessId;
    if (!bid) {
      toast({ title: "No business", description: "Your account must be linked to a business to record sales.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const createdIso = zambiaDateTimeToISO(createdAt);
      const saleBranchId =
        selectedBranchId !== "all" ? selectedBranchId : selectedItem?.branch_id ?? branches[0]?.id ?? null;

      const { error } = await supabase.from("sales").insert({
        item_name: itemName.trim() || null,
        item_id: itemId,
        quantity: parsedQuantity || null,
        unit_price: parsedUnitPrice || null,
        total_price: totalPrice || null,
        discount: parsedDiscount || null,
        final_amount: finalAmount || null,
        payment_method: paymentMethod || "Cash",
        created_at: createdIso,
        business_id: bid,
        branch_id: saleBranchId,
        sold_by: user.id,
      });

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        if (role !== "employee" || notificationPrefs.saleConfirmationEnabled) {
          toast({ title: "Entry saved", description: "The sale has been recorded and stock updated." });
        }
        setSelectedItem(null);
        setCustomItemName("");
        setQuantity("");
        setUnitPrice("");
        setDiscount("");
        fetchRowsForDate(selectedDate);
        fetchInventory();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fetchRowsForDate = async (dateStr: string) => {
    if (!effectiveBusinessId) return;
    setLoadingRows(true);

    const { start, end } = getZambiaDayBounds(dateStr);

    const baseQuery = supabase
      .from("sales")
      .select("id, item_name, quantity, unit_price, total_price, discount, final_amount, payment_method, created_at")
      .is("voided_at", null)
      .gte("created_at", start.toISOString())
      .lte("created_at", end.toISOString())
      .order("created_at", { ascending: true })
      .eq("business_id", effectiveBusinessId);
    const { data, error } = await baseQuery;

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setRows([]);
    } else {
      setRows((data ?? []) as LedgerRow[]);
    }
    setLoadingRows(false);
  };

  useEffect(() => {
    fetchRowsForDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, effectiveBusinessId]);

  const filteredRows = rows.filter((row) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (row.item_name || "").toLowerCase().includes(term);
  });

  const dailyTotal = filteredRows.reduce((sum, row) => {
    const value = row.final_amount ?? row.total_price ?? 0;
    return sum + Number(value);
  }, 0);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-display font-bold">Manual Ledger Entry</h2>
        <p className="text-sm text-muted-foreground">
          A simple digital mirror of the paper sales logbook.
        </p>
      </div>

      <Card className="border border-border max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base">Record Sale</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Item Name</Label>
              <Popover open={inventoryOpen} onOpenChange={setInventoryOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={inventoryOpen}
                    className={cn("w-full justify-between font-normal", !itemName && "text-muted-foreground")}
                  >
                    {itemName || "Select item from inventory (stock will deduct)..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search items..." />
                    <CommandList>
                      <CommandEmpty>No items in inventory.</CommandEmpty>
                      <CommandGroup>
                        {inventory.map((item) => (
                          <CommandItem
                            key={item.id}
                            value={item.name}
                            onSelect={() => {
                              setSelectedItem(item);
                              setCustomItemName("");
                              setUnitPrice(item.selling_price);
                              setInventoryOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", selectedItem?.id === item.id ? "opacity-100" : "opacity-0")} />
                            <span className="flex-1">{item.name}</span>
                            <span className="text-muted-foreground text-xs">Stock: {item.current_stock}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">Or enter custom item (no stock deduction):</p>
              <Input
                value={customItemName}
                onChange={(e) => {
                  setCustomItemName(e.target.value);
                  if (e.target.value) setSelectedItem(null);
                }}
                placeholder="e.g. Coca Cola 330ml"
              />
            </div>
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                min={0}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value === "" ? "" : Number(e.target.value))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Unit Price</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value === "" ? "" : Number(e.target.value))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Total Price</Label>
              <Input
                type="number"
                value={Number.isFinite(totalPrice) ? totalPrice.toFixed(2) : ""}
                readOnly
              />
            </div>
            <div className="space-y-2">
              <Label>Discount</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={discount}
                onChange={(e) => setDiscount(e.target.value === "" ? "" : Number(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Final Amount</Label>
              <Input
                type="number"
                value={Number.isFinite(finalAmount) ? finalAmount.toFixed(2) : ""}
                readOnly
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue placeholder="Cash" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Date &amp; Time</Label>
              <Input
                type="datetime-local"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                required
              />
            </div>
            <div className="md:col-span-2">
              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Save Entry"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-base">Daily Ledger</CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input
                type="date"
                className="w-40"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
              <Input
                type="text"
                className="w-48"
                placeholder="Search by item name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingRows ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredRows.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">
              {searchTerm ? "No transactions found." : "No entries recorded for this date."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2 font-semibold">Time</th>
                    <th className="text-left py-2 px-2 font-semibold">Item</th>
                    <th className="text-right py-2 px-2 font-semibold">Qty</th>
                    <th className="text-right py-2 px-2 font-semibold">Unit</th>
                    <th className="text-right py-2 px-2 font-semibold">Total</th>
                    <th className="text-right py-2 px-2 font-semibold">Discount</th>
                    <th className="text-left py-2 px-2 font-semibold">Payment</th>
                    <th className="text-right py-2 px-2 font-semibold">Final</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const time = formatInZambia(row.created_at, { timeStyle: "short" });
                    return (
                      <tr key={row.id} className="border-b border-border/40">
                        <td className="py-2 px-2">{time}</td>
                        <td className="py-2 px-2">{row.item_name || "—"}</td>
                        <td className="py-2 px-2 text-right">{row.quantity ?? "—"}</td>
                        <td className="py-2 px-2 text-right">
                          {row.unit_price != null ? `K${row.unit_price.toFixed(2)}` : "—"}
                        </td>
                        <td className="py-2 px-2 text-right">
                          {row.total_price != null ? `K${row.total_price.toFixed(2)}` : "—"}
                        </td>
                        <td className="py-2 px-2 text-right">
                          {row.discount != null ? `K${row.discount.toFixed(2)}` : "—"}
                        </td>
                        <td className="py-2 px-2">{row.payment_method || "Cash"}</td>
                        <td className="py-2 px-2 text-right font-semibold">
                          {row.final_amount != null ? `K${row.final_amount.toFixed(2)}` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t border-border font-semibold bg-muted/40">
                    <td className="py-2 px-2" colSpan={7}>
                      Daily Total
                    </td>
                    <td className="py-2 px-2 text-right">
                      K{dailyTotal.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

